import { Dispatch, SetStateAction, useEffect, useRef } from "react";
import { ShipmentModel } from "features/shared/shipmentCard.types";
import { authStore } from "entities/Auth";
import { createRoadSearchQueryParams, roadSearchCardStore } from "entities/RoadSearchCard";
import { searchFilterStore } from "entities/SearchFilter";
import { useAppDispatch, useAppSelector } from "shared/lib/redux";

import { roadSearchValueValidator } from "../utils";
import { useRoadSearch } from "./roadSearch.hook";

type UseRoadRequestStrategyReturnType = {
  handleSearch: (reference: string) => void;
};

type UseRoadRequestStrategyParams = {
  isReferenceEqualToOpenedCard: (reference: string) => boolean;
  searchValue: string;
  setErrorMessage: Dispatch<SetStateAction<string>>;
  openCard: (card: ShipmentModel) => void;
  showMainView: () => void;
  showIntroView: () => void;
  clearViewHistory: () => void;
};

export const useRoadRequestStrategy = ({
  isReferenceEqualToOpenedCard,
  searchValue,
  setErrorMessage,
  openCard,
  showMainView,
  showIntroView,
  clearViewHistory,
}: UseRoadRequestStrategyParams): UseRoadRequestStrategyReturnType => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(authStore.selectors.isAuthenticated);
  // const savedCards = useAppSelector(seaSavedCardStore.selectors.savedCards);
  const searchCards = useAppSelector(roadSearchCardStore.selectors.searchCards);
  const carrier = useAppSelector(searchFilterStore.selectors.carrier);
  const { roadSearchRequest, isRoadSearchLoading } = useRoadSearch({ isAuthenticated });

  const validatorMeta = useRef<ReturnType<typeof roadSearchValueValidator.validate>>(
    roadSearchValueValidator.validate(searchValue)
  );

  useEffect(() => {
    validatorMeta.current = roadSearchValueValidator.validate(searchValue);
  }, [searchValue]);

  const getRequestFuncByStrategy = (
    strategy: "authenticated" | "not-authenticated",
    reference: string
  ): (() => void) | null => {
    const carrierScacCode = carrier?.scacCodes[0];
    const reqParams = createRoadSearchQueryParams({
      requestType: "search",
      override: {
        number: reference,
        carrier: carrierScacCode,
      },
    });
    if (!reqParams) return null;

    switch (strategy) {
      case "authenticated":
        return () => {
          // roadSearchRequest(
          //   { where: { reference } },
          //   {
          //     onSettled: (response) => {
          //       if (response?.length) {
          //         const data = response[0];
          //         dispatch(roadSavedCardStore.actions.prependSavedCardAction({ data }));
          //         if (data?.reference) {
          //           // scrollToElement(data.reference);
          //           openCard(data);
          //         }
          //       } else {
          //         // const dlm = new DataLayersManager().searchInitiated();
          //         roadSearchRequest(reqParams, {
          //           onSuccess: (response) => {
          //             if (response.requestLimitReached) return;
          //             // dlm.setModel(response).handleSearch();
          //             dispatch(roadSearchCardStore.actions.prependSearchCardAction({ data: response }));
          //             if (response?.reference) {
          //               // scrollToElement(response.reference);
          //               openCard(response);
          //             }
          //           },
          //         });
          //       }
          //     },
          //   }
          // );
          roadSearchRequest(reqParams, {
            onWebAppRequestLimitReached: () => {
              if (searchCards.length > 0) {
                showMainView();
              } else {
                showIntroView();
              }
            },
            onSuccess: (response) => {
              if (response.requestLimitReached) return;

              // dlm.setModel(response).handleSearch();
              dispatch(roadSearchCardStore.actions.prependSearchCardAction({ data: response }));
              if (response?.reference) {
                // scrollToElement(response.reference);
                if (response.isSuccess) {
                  openCard(response);
                }
              }
            },
          });
        };
      case "not-authenticated":
      default:
        return () => {
          // const dlm = new DataLayersManager().searchInitiated();
          roadSearchRequest(reqParams, {
            onWebAppRequestLimitReached: () => {
              if (searchCards.length > 0) {
                showMainView();
              } else {
                showIntroView();
              }
            },
            onSuccess: (response) => {
              if (response.requestLimitReached) return;

              // dlm.setModel(response).handleSearch();
              dispatch(roadSearchCardStore.actions.prependSearchCardAction({ data: response }));
              if (response?.reference) {
                // scrollToElement(response.reference);
                if (response.isSuccess) {
                  openCard(response);
                }
              }
            },
          });
        };
    }
  };

  /**
   * 1. find existing search card OR
   * 2. if not authenticated - block search OR
   * 3. find existing user card OR
   * 4. search user card in DB OR
   * 5. run search and prepend search card to search cards list
   */
  const performRequest = async (reference: string) => {
    const targetSearchCard = searchCards.find((card) => card.reference === reference);
    if (targetSearchCard) {
      if (targetSearchCard?.reference && targetSearchCard.isSuccess) {
        openCard(targetSearchCard);
      }
      return;
    }

    // const targetSavedCard = savedCards.find((card) => card.reference === reference);
    // if (targetSavedCard) {
    //   if (targetSavedCard?.reference) {
    //     openCard(targetSavedCard);
    //   }
    //   return;
    // }

    if (isRoadSearchLoading) return;

    const requestFunc = getRequestFuncByStrategy(isAuthenticated ? "authenticated" : "not-authenticated", reference);

    requestFunc?.();
  };

  const handleSearch = (reference: string) => {
    const { isValid, errorMessage } = validatorMeta.current;

    if (isValid) {
      if (isReferenceEqualToOpenedCard(reference)) return;
      setErrorMessage("");
      performRequest(reference);
      clearViewHistory();
      return;
    }
    setErrorMessage(errorMessage);
  };

  return {
    handleSearch,
  };
};
